import { createApi } from "@reduxjs/toolkit/query/react";
import { baseProxyQueryWithReauth } from "./API";
import { pageProps } from "../../helpers/pageProps";

const el: any = document.querySelector("#app");
export const { contactsClientId, apiBase } = pageProps;

export type AssetType = {
  Uuid?: string;
  ClientId: number;
  Label: string;
  Description: string;
  Address: string;
  Reference: string;
  Kind: string;
  CreatedAt: string;
  UpdatedAt: string;
  HasLocation: boolean;
  HasManyLocations: boolean;
  CanMove: boolean;
  HasLocationHistory: boolean;
  Locations: any[];
  ParentAssets: AssetType[];
  ChildAssets: AssetType[];
  AssetContacts: any[];
  Stats?: {
    Locations: number;
    Contacts: number;
    Groups: number;
    Parents: number;
    Children: number;
    ActiveEvents: number;
  };
  Events?: any[];
};

export type AssetKindType = {
  ID?: number;
  ClientId?: number;
  Label: string;
  Value: string;
  Icon: string;
  IsDefault?: boolean;
  CreatedAt?: string;
  UpdatedAt?: string;
};

export type AssetImportOperation = "create" | "update" | "upsert";

export type AssetImportRow = {
  Reference: string;
  Label?: string;
  Type?: string;
  Description?: string;
  Address?: string;
  Contacts?: string | string[];
  ParentReferences?: string | string[];
  ChildReferences?: string | string[];
  GeoJSON?: any;
  LatLng?: string | number[];
  LngLat?: string | number[];
};

export type AssetImportRequest = {
  operation: AssetImportOperation;
  dryRun?: boolean;
  skipEmptyUpdate?: boolean;
  createContactsIfNotFound?: boolean;
  rows: AssetImportRow[];
};

export type AssetImportResponse = {
  dryRun: boolean;
  operation: AssetImportOperation;
  rowsReceived: number;
  rowsValid: number;
  assetsCreated: number;
  assetsUpdated: number;
  contactsCreated: number;
  relationsAdded: number;
  locationsSet: number;
  rowsFailed: number;
  rows: {
    row: number;
    reference?: string;
    assetUuid?: string;
    action?: string;
    errors?: string[];
  }[];
};

export type AssetImportSessionStatus =
  | "created"
  | "analysing"
  | "ready"
  | "queued"
  | "processing"
  | "completed"
  | "failed"
  | "cancelled";

export type AssetImportSession = {
  uuid: string;
  created_at: string;
  updated_at: string;
  workflow_file_uuid?: string;
  source: "upload" | "library" | "rows";
  status: AssetImportSessionStatus;
  operation: AssetImportOperation;
  filename: string;
  content_type: string;
  size_bytes: number;
  skip_empty_update: boolean;
  create_contacts_if_not_found: boolean;
  rows_received: number;
  rows_valid: number;
  rows_with_errors: number;
  rows_duplicate: number;
  rows_excluded: number;
  rows_ignored: number;
  rows_imported: number;
  assets_to_create: number;
  assets_to_update: number;
  contacts_matched: number;
  contacts_missing: number;
  contacts_created: number;
  relations_added: number;
  locations_set: number;
  failed_rows_during_run: number;
  error_message?: string;
};

export type AssetImportSessionSummary = AssetImportSession & {
  has_rows: boolean;
  rows_cleaned: boolean;
};

export type AssetImportContactMatch = {
  value: string;
  channel?: string;
  valid: boolean;
  matched: boolean;
  message?: string;
};

export type AssetImportSessionRow = {
  row_number: number;
  reference: string;
  status: "valid" | "error" | "duplicate" | "ignored" | "excluded" | "imported";
  action: string;
  asset_uuid?: string;
  duplicate_of_row_number?: number;
  raw?: any;
  normalized?: any;
  errors?: string[];
  contact_matches?: AssetImportContactMatch[];
  reference_matches?: any[];
};

export type AssetImportSessionCreateRequest = {
  operation: AssetImportOperation;
  skipEmptyUpdate?: boolean;
  createContactsIfNotFound?: boolean;
  source?: "upload" | "library" | "rows";
  workflowFileUuid?: string;
  rows?: AssetImportRow[];
};

export type AssetExportLocationFormat = "none" | "geojson" | "lat_lng" | "lng_lat";
export type AssetExportFormat = "csv" | "json";

export type AssetExportRequest = {
  search?: string;
  kind?: string;
  kinds?: string[];
  assets?: string[];
  ignore?: string[];
  hasLocation?: boolean;
  hasNoLocation?: boolean;
  hasContacts?: boolean;
  hasNoContacts?: boolean;
  hasDependants?: boolean;
  hasNoDependants?: boolean;
  hasConnected?: boolean;
  hasNoConnected?: boolean;
  orderBy?: string;
  order?: string;
  filename?: string;
  format?: AssetExportFormat;
  export_contacts?: boolean;
  export_relationships?: boolean;
  location_format?: AssetExportLocationFormat;
};

export type AssetExportResponse = {
  file: WorkflowFileListItem;
  session?: {
    uuid: string;
    status: "queued" | "processing" | "completed" | "failed";
    rows: number;
    error_message?: string;
    updated_at: string;
  };
  rows: number;
};

export type AssetDeleteRequest = {
  asset_uuids: string[];
  dependent_replacement_asset_uuid?: string;
  connected_replacement_asset_uuid?: string;
  contacts_replacement_asset_uuid?: string;
  monitors_replacement_asset_uuid?: string;
  archive_related_events?: boolean;
  confirmed?: boolean;
};

export type AssetDeleteAssetImpact = {
  asset_uuid: string;
  dependent_asset_uuids: string[];
  connected_asset_uuids: string[];
  contact_uuids: string[];
  group_uuids: string[];
  location_count: number;
  event_uuids: string[];
  running_event_uuids: string[];
  archive_event_uuids: string[];
  monitor_uuids: string[];
};

export type AssetDeletePreview = {
  assets: AssetType[];
  asset_uuids: string[];
  per_asset: Record<string, AssetDeleteAssetImpact>;
  dependent_asset_uuids: string[];
  connected_asset_uuids: string[];
  contact_uuids: string[];
  event_uuids: string[];
  running_event_uuids: string[];
  archive_event_uuids: string[];
  monitor_uuids: string[];
  totals: {
    assets: number;
    dependent_assets: number;
    connected_assets: number;
    contacts: number;
    groups: number;
    locations: number;
    events: number;
    running_events: number;
    archive_events: number;
    monitors: number;
  };
  dependent_replacement_asset_uuid?: string;
  connected_replacement_asset_uuid?: string;
  contacts_replacement_asset_uuid?: string;
  monitors_replacement_asset_uuid?: string;
  archive_related_events: boolean;
};

export type AssetDeleteResult = {
  preview: AssetDeletePreview;
  deleted_asset_uuids: string[];
  contacts_reassigned: number;
  monitors_reassigned: number;
  events_archived: number;
};

export type WorkflowFileListItem = {
  uuid: string;
  filename: string;
  content_type: string;
  extension: string;
  type_group: string;
  size_bytes: number;
  scope: string;
  created_at: string;
  updated_at: string;
  directories?: string[];
  asset_import?: {
    session_uuid: string;
    status: AssetImportSessionStatus;
    active: boolean;
    rows_received: number;
    rows_valid: number;
    rows_with_errors: number;
    rows_duplicate: number;
    rows_excluded: number;
    rows_imported: number;
    has_rows?: boolean;
    error_message?: string;
    updated_at: string;
  };
  asset_export?: {
    session_uuid: string;
    status: "queued" | "processing" | "completed" | "failed";
    active: boolean;
    rows: number;
    error_message?: string;
    updated_at: string;
  };
};

export type WorkflowFileUploadRequest = File | {
  file: File;
  temporary?: boolean;
};

export type LocationClusterType = {
  X: number;
  Y: number;
  IsCluster: boolean;
  ItemCount: number;
  Item: LocationType;
  ActiveEventAssetsCount?: number;
  ActiveEventsSeverity: {
    [key: string]: number;
  };
};

export type LocationType = {
  Uuid?: string;
  Description?: string;
  AssetUuid?: string;
  EventUuid?: string;
  ContactUuid?: string;
  IsCurrent: boolean;
  CreatedAt?: string;
  IsShape: boolean;
  IsDirect?: boolean;
  Contact?: any;
  Asset?: AssetType;
  Area?: {
    type: "MultiPolygon";
    coordinates: number[][][][]; // [ [ [ [ 1, 2 ], [ 3, 4 ] ] ] ]
  };
  Point?: {
    type: "Point";
    coordinates: number[]; // [ 1, 2 ]
  };
  Event?: any;
};

export type WeatherAlertRuleType = {
  ID: number;
  CreatedAt: string;
  ShapeId: number;
  GroupName: string;
  ClientId: number;
  Active: boolean;
  Priority: number;
  Description: string;
  Stop: boolean;
  Conditions: {
    ID: number;
    CreatedAt: string;
    AlertId: number;
    ClientId: number;
    Condition: string;
    ConditionOperator: string;
    ConditionValue: number;
    Description: string;
    Color: string;
    Order: number;
  }[];
  Shapes: null;
};

export type AssetsMapRequestType = {
  UseAssets?: boolean;
  UseContacts?: boolean;
  Coordinates?: number[][][]; // GeoJSON MultiPolygon coordinates
  IncludeAssets?: string[]; // optional list of asset UUIDs to include
  ExcludeAssets?: string[]; // optional list of asset UUIDs to exclude
  IncludeContacts?: string[]; // optional list of contact UUIDs to include
  ExcludeContacts?: string[]; // optional list of contact UUIDs to exclude
  IncludeGroups?: string[]; // optional list of group UUIDs to include
  ExcludeGroups?: string[]; // optional list of group UUIDs to exclude
  Page?: number;
  PerPage?: number;
};

// Define a service using a base URL and expected endpoints
export const mapsAPI = createApi({
  reducerPath: "mapsAPI",
  baseQuery: baseProxyQueryWithReauth("logic/workflows/api"),
  refetchOnMountOrArgChange: true,
  tagTypes: [
    "Assets",
    "Locations",
    "Contacts",
    "Groups",
    "ContactLocations",
    "AssetLocations",
    "WeatherAlerts",
    "WeatherAlertRules",
    "WeatherForecast",
    "AssetTypes",
    "WorkflowFiles",
  ],
  endpoints: (builder) => ({
    getAssets: builder.query<
      { assets: AssetType[]; total: number },
      {
        page?: number;
        perPage?: number;
        search?: string;
        kind?: string;
        kinds?: string[];
        assets?: string[];
        ignore?: string[];
        hasLocation?: boolean;
        hasNoLocation?: boolean;
        hasContacts?: boolean;
        hasNoContacts?: boolean;
        hasDependants?: boolean;
        hasNoDependants?: boolean;
        hasConnected?: boolean;
        hasNoConnected?: boolean;
        hasActiveEvents?: boolean;
        hasNoActiveEvents?: boolean;
        orderBy?: string;
        order?: string;
      }
    >({
      query: (params) => ({
        url: `assets`,
        method: "GET",
        params,
      }),
      providesTags: ["Assets"],
    }),
    getAsset: builder.query<AssetType, string>({
      query: (uuid) => `assets/${uuid}`,
      transformResponse: (response: { asset: AssetType }, meta, arg) =>
        response.asset,
      providesTags: ["Assets"],
    }),
    upsertAsset: builder.mutation<any, Partial<AssetType>>({
      query: (body) => ({
        url: `assets`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Assets", "AssetTypes"],
    }),
    previewAssetImport: builder.mutation<
      AssetImportResponse,
      AssetImportRequest
    >({
      query: (body) => ({
        url: `assets/import/preview`,
        method: "POST",
        body,
      }),
    }),
    runAssetImport: builder.mutation<AssetImportResponse, AssetImportRequest>({
      query: (body) => ({
        url: `assets/import`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Assets", "AssetTypes", "Locations", "Contacts"],
    }),
    createAssetImportSession: builder.mutation<
      AssetImportSession,
      AssetImportSessionCreateRequest
    >({
      query: (body) => ({
        url: `assets/import/sessions`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["WorkflowFiles"],
    }),
    getAssetImportSession: builder.query<AssetImportSession, string>({
      query: (uuid) => `assets/import/sessions/${uuid}`,
      providesTags: (_result, _error, uuid) => [{ type: "WorkflowFiles", id: `asset-import-${uuid}` }],
    }),
    getAssetImportSessionRows: builder.query<
      { rows: AssetImportSessionRow[]; total: number },
      { uuid: string; limit?: number; offset?: number; status?: string }
    >({
      query: ({ uuid, limit = 10, offset = 0, status }) => ({
        url: `assets/import/sessions/${uuid}/rows`,
        method: "GET",
        params: { limit, offset, status },
      }),
      providesTags: (_result, _error, arg) => [{ type: "WorkflowFiles", id: `asset-import-rows-${arg.uuid}` }],
    }),
    updateAssetImportSessionRowExclusions: builder.mutation<
      AssetImportSession,
      { uuid: string; rowNumbers: number[]; excluded: boolean }
    >({
      query: ({ uuid, rowNumbers, excluded }) => ({
        url: `assets/import/sessions/${uuid}/rows/exclusions`,
        method: "PATCH",
        body: { rowNumbers, excluded },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "WorkflowFiles", id: `asset-import-${arg.uuid}` },
        { type: "WorkflowFiles", id: `asset-import-rows-${arg.uuid}` },
      ],
    }),
    deleteAssetImportSessionErrorRows: builder.mutation<
      AssetImportSession,
      { uuid: string; rowNumbers?: number[] }
    >({
      query: ({ uuid, rowNumbers = [] }) => ({
        url: `assets/import/sessions/${uuid}/rows/errors`,
        method: "DELETE",
        body: { rowNumbers },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "WorkflowFiles", id: `asset-import-${arg.uuid}` },
        { type: "WorkflowFiles", id: `asset-import-rows-${arg.uuid}` },
      ],
    }),
    runAssetImportSession: builder.mutation<AssetImportSession, string>({
      query: (uuid) => ({
        url: `assets/import/sessions/${uuid}/run`,
        method: "POST",
      }),
      invalidatesTags: ["Assets", "AssetTypes", "Locations", "Contacts", "WorkflowFiles"],
    }),
    getAssetImportSessions: builder.query<
      { sessions: AssetImportSessionSummary[] },
      { limit?: number } | void
    >({
      query: (params) => ({
        url: `assets/import/sessions`,
        method: "GET",
        params,
      }),
      providesTags: ["WorkflowFiles"],
    }),
    exportAssets: builder.mutation<AssetExportResponse, AssetExportRequest>({
      query: (body) => ({
        url: `assets/export`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["WorkflowFiles"],
    }),
    previewAssetDelete: builder.mutation<
      { preview: AssetDeletePreview },
      AssetDeleteRequest
    >({
      query: (body) => ({
        url: `assets/delete-preview`,
        method: "POST",
        body,
        hideToasts: true,
      }),
    }),
    deleteAssets: builder.mutation<
      { result: AssetDeleteResult },
      AssetDeleteRequest
    >({
      query: (body) => ({
        url: `assets/delete`,
        method: "POST",
        body,
        hideToasts: true,
      }),
      invalidatesTags: ["Assets", "Locations", "Contacts", "Groups"],
    }),
    getWorkflowFiles: builder.query<
      { files: WorkflowFileListItem[]; total: number },
      { page?: number; perPage?: number; search?: string; extensions?: string[] }
    >({
      query: ({ page = 1, perPage = 50, search = "", extensions = ["csv", "json"] }) => ({
        url: `workflow-files`,
        method: "GET",
        params: {
          page,
          perPage,
          search,
          extensions,
          scope: "permanent",
        },
      }),
      providesTags: ["WorkflowFiles"],
    }),
    uploadWorkflowFile: builder.mutation<WorkflowFileListItem, WorkflowFileUploadRequest>({
      query: (request) => {
        const file = request instanceof File ? request : request.file;
        const temporary = request instanceof File ? false : !!request.temporary;
        const body = new FormData();
        body.append("file", file);
        body.append("filename", file.name);
        if (temporary) body.append("temporary", "true");
        return {
          url: `workflow-files`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["WorkflowFiles"],
    }),
    getWorkflowFileText: builder.mutation<string, string>({
      query: (uuid) => ({
        url: `workflow-files/${uuid}/download`,
        method: "GET",
        responseHandler: "text",
      }),
    }),
    getAssetTypes: builder.query<
      { types: AssetKindType[]; hasMore: boolean },
      { limit?: number; offset?: number }
    >({
      query: (params) => ({
        url: `asset-types`,
        method: "GET",
        params,
      }),
      providesTags: ["AssetTypes"],
    }),
    upsertAssetType: builder.mutation<
      { type: AssetKindType },
      Partial<AssetKindType>
    >({
      query: (body) => ({
        url: `asset-types`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["AssetTypes"],
    }),
    deleteAssetType: builder.mutation<
      any,
      { value: string; replacementValue?: string }
    >({
      query: ({ value, replacementValue }) => ({
        url: `asset-types/${encodeURIComponent(value)}`,
        method: "DELETE",
        body: replacementValue ? { ReplacementValue: replacementValue } : undefined,
      }),
      invalidatesTags: ["AssetTypes", "Assets"],
    }),
    setAsParentAsset: builder.mutation<
      any,
      { children: string[]; assetUuid: string }
    >({
      query: ({ children, assetUuid }) => ({
        url: `assets/set-as-parent/${assetUuid}`,
        method: "POST",
        body: children,
      }),
      invalidatesTags: ["Assets"],
    }),
    addChildrenToParentAsset: builder.mutation<
      any,
      { children: string[]; assetUuid: string }
    >({
      query: ({ children, assetUuid }) => ({
        url: `assets/associations/add-parent/${assetUuid}`,
        method: "POST",
        body: children,
      }),
      invalidatesTags: ["Assets"],
    }),
    removeChildrenFromParentAsset: builder.mutation<
      any,
      { children: string[]; assetUuid: string }
    >({
      query: ({ children, assetUuid }) => ({
        url: `assets/associations/remove-parent/${assetUuid}`,
        method: "POST",
        body: children,
      }),
      invalidatesTags: ["Assets"],
    }),
    setAsChildAsset: builder.mutation<
      any,
      { parents: string[]; assetUuid: string }
    >({
      query: ({ parents, assetUuid }) => ({
        url: `assets/set-as-child/${assetUuid}`,
        method: "POST",
        body: parents,
      }),
      invalidatesTags: ["Assets"],
    }),
    addParentsToChildAsset: builder.mutation<
      any,
      { parents: string[]; assetUuid: string }
    >({
      query: ({ parents, assetUuid }) => ({
        url: `assets/associations/add-child/${assetUuid}`,
        method: "POST",
        body: parents,
      }),
      invalidatesTags: ["Assets"],
    }),
    removeParentsFromChildAsset: builder.mutation<
      any,
      { parents: string[]; assetUuid: string }
    >({
      query: ({ parents, assetUuid }) => ({
        url: `assets/associations/remove-child/${assetUuid}`,
        method: "POST",
        body: parents,
      }),
      invalidatesTags: ["Assets"],
    }),

    getChildrenAssets: builder.query<
      { assets: AssetType[]; total: number },
      string
    >({
      query: (uuid) => `assets/children/${uuid}?perPage=100`,
      providesTags: ["Assets"],
    }),
    getParentAssets: builder.query<
      { assets: AssetType[]; total: number },
      string
    >({
      query: (uuid) => `assets/parents/${uuid}?perPage=100`,
      providesTags: ["Assets"],
    }),
    getLocations: builder.query<
      { items: LocationClusterType[]; total: number },
      any
    >({
      query: (body) => ({
        url:
          body.Contacts && !body.Assets
            ? `locations/contacts/map`
            : `locations/map`,
        method: "POST",
        body,
      }),
      providesTags: ["Locations"],
    }),
    getLocationsWithoutClustering: builder.query<
      { items: LocationType[]; total: number },
      any
    >({
      query: (body) => ({
        url: "locations/map-data",
        method: "POST",
        body,
      }),
      providesTags: ["Locations"],
    }),
    getLocationsWithAssets: builder.query<
      { items: LocationType[]; total: number },
      any
    >({
      query: (body) => ({
        url: "locations/with-assets",
        method: "POST",
        body,
      }),
      providesTags: ["Locations"],
    }),
    getWeatherAlertsMap: builder.query<
      any,
      {
        west: number;
        north: number;
        east: number;
        south: number;
        centerLng: number;
        conditions?: string[];
        conditionIds?: number[];
        search?: string;
        start?: string;
        end?: string;
        radius?: number;
      }
    >({
      query: (params) => {
        return {
          url: `geo/map/alerts`,
          params,
        };
      },
      providesTags: ["WeatherAlerts"],
    }),
    getWeatherForecastMap: builder.query<
      any,
      {
        west: number;
        north: number;
        east: number;
        south: number;
        centerLng: number;
        start?: string;
        radius?: number;
        steps?: number; // number of 3 hour intervals
      }
    >({
      query: (params) => {
        return {
          url: `geo/map/weather-forecast`,
          params,
        };
      },
      providesTags: ["WeatherForecast"],
    }),
    getWeatherAlertsRules: builder.query<
      { alerts: WeatherAlertRuleType[]; total: number },
      any
    >({
      query: () => `geo/alerts/rules`,
      providesTags: ["WeatherAlertRules"],
    }),
    getAssetLocations: builder.query<
      { locations: LocationType[]; total: number },
      { uuid: string; page: number; perPage: number }
    >({
      query: ({ uuid, ...params }) => ({
        url: `locations/asset/${uuid}`,
        params,
      }),
      providesTags: ["Locations"],
    }),
    getAssetsByContact: builder.query<AssetType[], string>({
      query: (contactUuid) => `/assets/by-contact/${contactUuid}`,
      providesTags: ["Assets"],
      transformResponse: (response: { assets: AssetType[] }, meta, arg) =>
        response.assets,
    }),
    getContactsByAsset: builder.query<
      { contacts: any[]; total: number },
      { assetUuid: string; page: number; perPage: number }
    >({
      query: ({ assetUuid, page, perPage }) =>
        `/assets/contacts/${assetUuid}?page=${page}&perPage=${perPage}`,
      providesTags: ["Contacts"],
    }),
    getContactLocations: builder.query<LocationType[], string>({
      query: (contactUuid) => `locations/contact/${contactUuid}`,
      providesTags: ["Locations"],
      transformResponse: (response: { locations: LocationType[] }, meta, arg) =>
        response.locations,
    }),
    upsertAssetLocations: builder.mutation<
      any,
      { assetUuid: string; body: LocationType[] }
    >({
      query: ({ assetUuid, body }) => ({
        url: `locations/asset/${assetUuid}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Locations", "Assets"],
    }),
    deleteAssetLocations: builder.mutation<
      any,
      { assetUuid: string; body: string[] }
    >({
      query: ({ assetUuid, body }) => ({
        url: `locations-delete/asset/${assetUuid}`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Locations", "Assets"],
    }),
    setContactsInLocation: builder.mutation<
      any,
      { Lat: number; Lng: number; items: string[] }
    >({
      query: (body) => ({
        url: `locations/contact`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["Locations"],
    }),

    assignContactsToAsset: builder.mutation<
      any,
      { assetUuid: string; contactUuids: string[]; isDelete?: boolean }
    >({
      query: ({ assetUuid, contactUuids, isDelete }) => ({
        url: `assets/assign-contacts/${assetUuid}${isDelete ? "?delete" : ""}`,
        method: "POST",
        body: contactUuids,
      }),
      invalidatesTags: ["Assets", "Contacts"],
    }),

    removeContactsFromAsset: builder.mutation<
      any,
      { assetUuid: string; contactUuids: string[] }
    >({
      query: ({ assetUuid, contactUuids }) => ({
        url: `/assets/remove-contacts/${assetUuid}`,
        method: "POST",
        body: contactUuids,
      }),
      invalidatesTags: ["Assets", "Contacts"],
    }),

    removeLocationFromContacts: builder.mutation<
      any,
      { contactUuids: string[] }
    >({
      query: ({ contactUuids }) => ({
        url: `/locations/contact`,
        method: "POST",
        body: { Clear: true, Items: contactUuids },
      }),
      invalidatesTags: ["Contacts", "Locations"],
    }),

    removeAllContactsFromAsset: builder.mutation<any, { assetUuid: string }>({
      query: ({ assetUuid }) => ({
        url: `/assets/remove-contacts/${assetUuid}?delete=all `,
        method: "POST",
        body: [],
      }),
      invalidatesTags: ["Assets", "Contacts"],
    }),

    getGroupsByAsset: builder.query<
      { groups: any[]; total: number },
      { assetUuid: string; page: number; perPage: number }
    >({
      query: ({ assetUuid, page, perPage }) =>
        `/assets/groups/${assetUuid}?page=${page}&perPage=${perPage}`,
      providesTags: ["Groups"],
    }),

    assignGroupsToAsset: builder.mutation<
      any,
      { assetUuid: string; groupUuids: string[]; isDelete?: boolean }
    >({
      query: ({ assetUuid, groupUuids, isDelete }) => ({
        url: `assets/assign-groups/${assetUuid}${isDelete ? "?delete" : ""}`,
        method: "POST",
        body: groupUuids,
      }),
      invalidatesTags: ["Assets", "Groups"],
    }),

    removeGroupsFromAsset: builder.mutation<
      any,
      { assetUuid: string; groupUuids: string[] }
    >({
      query: ({ assetUuid, groupUuids }) => ({
        url: `/assets/remove-groups/${assetUuid}`,
        method: "POST",
        body: groupUuids,
      }),
      invalidatesTags: ["Assets", "Groups"],
    }),

    listContactsAndGroupsInLocation: builder.query<any, AssetsMapRequestType>({
      query: (body) => ({
        url: `assets-map/contacts-and-groups`,
        method: "POST",
        body,
      }),
      providesTags: ["ContactLocations"],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetAssetsQuery,
  useLazyGetAssetsQuery,
  useGetAssetQuery,
  useGetAssetTypesQuery,
  useGetAssetsByContactQuery,
  useGetContactsByAssetQuery,
  useUpsertAssetMutation,
  usePreviewAssetImportMutation,
  useRunAssetImportMutation,
  useCreateAssetImportSessionMutation,
  useGetAssetImportSessionQuery,
  useGetAssetImportSessionRowsQuery,
  useUpdateAssetImportSessionRowExclusionsMutation,
  useDeleteAssetImportSessionErrorRowsMutation,
  useRunAssetImportSessionMutation,
  useGetAssetImportSessionsQuery,
  useExportAssetsMutation,
  usePreviewAssetDeleteMutation,
  useDeleteAssetsMutation,
  useGetWorkflowFilesQuery,
  useUploadWorkflowFileMutation,
  useGetWorkflowFileTextMutation,
  useUpsertAssetTypeMutation,
  useDeleteAssetTypeMutation,
  useGetChildrenAssetsQuery,
  useGetParentAssetsQuery,
  useSetAsChildAssetMutation,
  useSetAsParentAssetMutation,
  useGetLocationsQuery,
  useGetWeatherAlertsMapQuery,
  useGetWeatherAlertsRulesQuery,
  useUpsertAssetLocationsMutation,
  useDeleteAssetLocationsMutation,
  useGetAssetLocationsQuery,
  useGetContactLocationsQuery,
  useSetContactsInLocationMutation,
  useAssignContactsToAssetMutation,
  useRemoveContactsFromAssetMutation,
  useRemoveAllContactsFromAssetMutation,
  useRemoveLocationFromContactsMutation,
  useGetWeatherForecastMapQuery,
  useAddChildrenToParentAssetMutation,
  useRemoveChildrenFromParentAssetMutation,
  useAddParentsToChildAssetMutation,
  useRemoveParentsFromChildAssetMutation,
  useGetLocationsWithoutClusteringQuery,
  useGetGroupsByAssetQuery,
  useAssignGroupsToAssetMutation,
  useRemoveGroupsFromAssetMutation,
  useGetLocationsWithAssetsQuery,
  useListContactsAndGroupsInLocationQuery,
} = mapsAPI;
